import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Table, Button, Tag, Space, Drawer, Descriptions, message,
  Modal, Select, Typography, Tooltip, Input, Radio,
} from 'antd';
import {
  SyncOutlined, CopyOutlined, CheckOutlined, LinkOutlined,
  ReloadOutlined, SearchOutlined,
} from '@ant-design/icons';
import { emailApi, orderApi, appealApi, claimApi } from '../services/api';
import { fmtCN, cnDay } from '../utils/time';

const { Text } = Typography;

// 明细区固定高度：内容长短变化不撑开表单，只在内部滚动
const DETAIL_HEIGHT = 420;

export default function InboxEmails() {
  const [mails, setMails] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [selectedIds, setSelectedIds] = useState([]);
  const [batchLoading, setBatchLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [onlyUnread, setOnlyUnread] = useState(false);
  const [keywordInput, setKeywordInput] = useState('');
  const [keyword, setKeyword] = useState('');
  const [current, setCurrent] = useState(null);
  const [matchOpen, setMatchOpen] = useState(false);
  const [matchKind, setMatchKind] = useState('order'); // order | appeal | claim
  const [matchOrderId, setMatchOrderId] = useState(null);
  const [matching, setMatching] = useState(false);
  const [orderOptions, setOrderOptions] = useState([]);
  const [orderSearch, setOrderSearch] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [translating, setTranslating] = useState(false);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const data = await emailApi.listUnidentified({
        only_unread: onlyUnread,
        keyword: keyword.trim() || undefined,
        page,
        page_size: pageSize,
      });
      setMails(data?.items || []);
      setTotal(data?.total || 0);
      const cnt = await emailApi.unreadCount();
      setUnreadCount(cnt.count || 0);
    } catch (e) {} finally {
      setLoading(false);
    }
  }, [onlyUnread, keyword, page, pageSize]);

  useEffect(() => { fetchList(); }, [fetchList]);
  // 每 60 秒自动刷新（后端后台每 5 分钟同步一次 IMAP）
  useEffect(() => {
    const t = setInterval(fetchList, 60000);
    return () => clearInterval(t);
  }, [fetchList]);

  // 搜索/筛选条件变化时回到第 1 页
  useEffect(() => { setPage(1); }, [keyword, onlyUnread]);

  // 搜索输入防抖 400ms，避免逐字请求
  useEffect(() => {
    const t = setTimeout(() => setKeyword(keywordInput), 400);
    return () => clearTimeout(t);
  }, [keywordInput]);

  const handleBatchRead = async () => {
    if (!selectedIds.length) return;
    const ids = selectedIds;
    // 乐观更新：选中的立即变灰
    setMails((prev) => prev.map((m) => (ids.includes(m.id) ? { ...m, is_read: true, status: 'read' } : m)));
    setSelectedIds([]);
    setBatchLoading(true);
    try {
      await emailApi.batchRead(ids);
      message.success(`已将 ${ids.length} 封邮件标记为已读`);
      fetchList();
    } catch (e) {} finally {
      setBatchLoading(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const r = await emailApi.sync();
      message.success(`同步完成：收取 ${r.fetched} 封，匹配订单 ${r.matched} 封，待识别 ${r.unidentified} 封`);
      fetchList();
    } catch (e) {} finally {
      setSyncing(false);
    }
  };

  const handleCopySubject = async (mail) => {
    try {
      await navigator.clipboard.writeText(mail.subject || '');
      message.success('标题已复制');
    } catch (e) {
      message.warning('复制失败，请手动选择复制');
    }
  };

  const handleMarkRead = async (mail) => {
    // 乐观更新：立即变灰
    setMails((prev) => prev.map((m) => (m.id === mail.id ? { ...m, is_read: true, status: 'read' } : m)));
    await emailApi.markRead(mail.id);
    message.success('已标记为已读');
    if (current?.id === mail.id) setCurrent({ ...mail, is_read: true, status: 'read' });
    fetchList();
  };

  // AI 翻译（手动点击触发；force=true 表示重新翻译）
  const handleTranslate = async (mail, force = false) => {
    setTranslating(true);
    try {
      const r = await emailApi.translate(mail.id, force);
      setCurrent({ ...mail, translation: r.translation });
      setMails((prev) => prev.map((m) => (m.id === mail.id ? { ...m, translation: r.translation } : m)));
      message.success(force ? '已重新翻译' : '翻译完成');
    } catch (e) {
      message.error(e?.response?.data?.detail || e?.message || '翻译失败，请稍后重试');
    } finally {
      setTranslating(false);
    }
  };

  // 匹配工单：按关键字搜索系统已有工单（调度/申诉/索赔三类）
  const KIND_LABEL = { order: '调度工单', appeal: '申诉工单', claim: '索赔工单' };
  const STATUS_LABEL = (s) => (s === 'pending' ? '待跟进' : s === 'processing' ? '处理中' : s === 'awaiting_customer' ? '待客服处理' : '已完成');
  const searchOrders = async (kw, kind = matchKind) => {
    setOrderSearch(kw);
    try {
      let items = [];
      if (kind === 'order') {
        const data = await orderApi.list({ status: 'all', keyword: kw || undefined, page_size: 20 });
        items = (data.items || []).map((o) => ({ value: o.id, label: `${o.order_no}（${o.customer_name || '-'} / ${STATUS_LABEL(o.status)}）` }));
      } else if (kind === 'appeal') {
        const data = await appealApi.list({ keyword: kw || undefined, limit: 20 });
        items = (data.items || []).map((o) => ({ value: o.id, label: `${o.appeal_no}（${o.customer_name || '-'} / ${STATUS_LABEL(o.status)}）` }));
      } else {
        const data = await claimApi.list({ keyword: kw || undefined, limit: 20 });
        items = (data.items || []).map((o) => ({ value: o.id, label: `${o.claim_no}（${o.customer_name || '-'} / ${STATUS_LABEL(o.status)}）` }));
      }
      setOrderOptions(items);
    } catch (e) {}
  };

  const openMatch = async (mail) => {
    setCurrent(mail);
    setMatchOrderId(null);
    setMatchKind('order');
    setMatchOpen(true);
    searchOrders(mail.bol_no || '', 'order');
  };

  const handleMatch = async () => {
    if (!matchOrderId) {
      message.warning('请先选择要匹配的工单');
      return;
    }
    setMatching(true);
    try {
      const payload = matchKind === 'order' ? { order_id: matchOrderId } : matchKind === 'appeal' ? { appeal_id: matchOrderId } : { claim_id: matchOrderId };
      const r = await emailApi.matchEmail(current.id, payload);
      message.success(r.moved_to_pending
        ? '匹配成功：翻译已并入工单日志，工单已自动流转至待客服处理'
        : `匹配成功：翻译已并入${KIND_LABEL[matchKind]}日志`);
      setMatchOpen(false);
      fetchList();
    } catch (e) {} finally {
      setMatching(false);
    }
  };

  const columns = [
    {
      title: '接收时间',
      dataIndex: 'received_at',
      key: 'received_at',
      width: 140,
      render: (t, r) => fmtCN(t || r.created_at, 'MM-DD HH:mm'),
      sorter: (a, b) => cnDay(a.received_at || a.created_at).valueOf() - cnDay(b.received_at || b.created_at).valueOf(),
      defaultSortOrder: 'descend',
    },
    {
      title: '发件人',
      dataIndex: 'sender',
      key: 'sender',
      width: 180,
      ellipsis: true,
    },
    {
      title: '邮件主题',
      dataIndex: 'subject',
      key: 'subject',
      ellipsis: true,
      render: (s, r) => (
        <Space size={4}>
          <span style={{ width: 8, height: 8, borderRadius: 4, display: 'inline-block', background: r.is_read ? '#d9d9d9' : '#ff4d4f' }} />
          <Text strong={!r.is_read} style={{ cursor: 'pointer' }} onClick={() => setCurrent(r)}>{s || '（无主题）'}</Text>
        </Space>
      ),
    },
    {
      title: '匹配状态',
      key: 'matched_status',
      width: 120,
      render: (_, r) => {
        if (r.matched_order_id) return <Tag color="geekblue">已匹配-调度</Tag>;
        if (r.matched_appeal_id) return <Tag color="purple">已匹配-申诉</Tag>;
        if (r.matched_claim_id) return <Tag color="magenta">已匹配-索赔</Tag>;
        return <Tag color="orange">未匹配</Tag>;
      },
    },
    {
      title: '状态',
      dataIndex: 'is_read',
      key: 'is_read',
      width: 90,
      render: (_, r) => (r.is_read
        ? <Tag color="default">已读</Tag>
        : <Tag color="red">待识别</Tag>),
    },
    {
      title: '操作',
      key: 'action',
      width: 240,
      fixed: 'right',
      render: (_, r) => {
        const isMatched = !!(r.matched_order_id || r.matched_appeal_id || r.matched_claim_id || r.status === 'matched');
        return (
          <Space size={4}>
            {isMatched
              ? <Tooltip title="已匹配工单，如需改配请进入邮件详情操作">
                  <Button size="small" disabled icon={<LinkOutlined />}>已匹配</Button>
                </Tooltip>
              : <Button size="small" type="primary" icon={<LinkOutlined />} onClick={() => openMatch(r)}>匹配工单</Button>}
            <Tooltip title="复制标题">
              <Button size="small" icon={<CopyOutlined />} onClick={() => handleCopySubject(r)} />
            </Tooltip>
            {r.is_read
              ? <Button size="small" disabled icon={<CheckOutlined />}>已读</Button>
              : <Button size="small" icon={<CheckOutlined />} onClick={() => handleMarkRead(r)}>已读</Button>}
          </Space>
        );
      },
    },
  ];

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h2>
            待识别邮件
            {unreadCount > 0 && (
              <Tag color="red" style={{ marginLeft: 8 }} className="stat-flash">未读 {unreadCount}</Tag>
            )}
          </h2>
          <p className="desc">
            IMAP 每 5 分钟自动收取邮件；系统扫描主题与正文自动匹配工单并流转。显式前缀（最高优先级）：dispatch-编号→调度工单，dispute-编号→申诉工单，claim-编号→索赔工单（编号≥6位，分隔符支持 - _ ： 空格）。无前缀时回退模糊匹配：BOL#/PRO#/LOAD#/ORDER#/PICK UP# 等业务前缀编号，PHX/PHO/WWW 开头紧跟数字的编号，以及长数字/多段横杠单号。匹配不准可在操作列手动改配（以手动为准，三类互斥）。
          </p>
        </div>
        <Space>
          <Button icon={<SyncOutlined />} loading={syncing} onClick={handleSync}>立即同步邮件</Button>
          <Button icon={<ReloadOutlined />} onClick={fetchList}>刷新</Button>
        </Space>
      </div>

      <Card className="card-shadow" style={{ marginBottom: 16 }}>
        <Space wrap>
          <Input
            placeholder="搜索主题/发件人/BOL号"
            prefix={<SearchOutlined />}
            value={keywordInput}
            onChange={(e) => setKeywordInput(e.target.value)}
            allowClear
            style={{ width: 280 }}
          />
          <Select
            value={onlyUnread ? 'unread' : 'all'}
            onChange={(v) => setOnlyUnread(v === 'unread')}
            style={{ width: 140 }}
            options={[
              { value: 'all', label: '全部邮件' },
              { value: 'unread', label: '只看未读' },
            ]}
          />
          <Button
            icon={<CheckOutlined />}
            disabled={!selectedIds.length}
            loading={batchLoading}
            onClick={handleBatchRead}
          >
            批量已读{selectedIds.length ? `（${selectedIds.length}）` : ''}
          </Button>
        </Space>
      </Card>

      <Card className="card-shadow">
        <Table
          columns={columns}
          dataSource={mails}
          rowKey="id"
          loading={loading}
          rowSelection={{
            selectedRowKeys: selectedIds,
            onChange: (keys) => setSelectedIds(keys),
          }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            pageSizeOptions: [10, 20, 50, 100],
            showTotal: (t) => `共 ${t} 条`,
            onChange: (p, ps) => {
              setPage(p);
              setPageSize(ps);
              setSelectedIds([]);
            },
          }}
          scroll={{ x: 1200 }}
          onRow={(r) => ({
            onClick: (e) => {
              // 勾选 checkbox 时不打开邮件抽屉
              if (e.target.closest('.ant-checkbox-wrapper') || e.target.closest('.ant-table-selection-column')) return;
              setCurrent(r);
            },
            style: { cursor: 'pointer' },
          })}
        />
      </Card>

      {/* 明细抽屉：加宽，原文(英)/译文(中) 左右对照，各自独立滚动 */}
      <Drawer
        title="邮件明细"
        open={!!current}
        onClose={() => setCurrent(null)}
        width={Math.min(1200, typeof window !== 'undefined' ? window.innerWidth * 0.92 : 1200)}
        extra={
          current && (
            <Space>
              <Button type="primary" icon={<LinkOutlined />} onClick={() => openMatch(current)}>匹配工单</Button>
              <Button icon={<CopyOutlined />} onClick={() => handleCopySubject(current)}>复制标题</Button>
              {current.is_read
                ? <Button icon={<CheckOutlined />} disabled>已读</Button>
                : <Button icon={<CheckOutlined />} onClick={() => handleMarkRead(current)}>已读</Button>}
            </Space>
          )
        }
      >
        {current && (
          <>
            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label="发件人">{current.sender || '-'}</Descriptions.Item>
              <Descriptions.Item label="主题">
                <Space><span>{current.subject || '-'}</span><Button size="small" type="link" icon={<CopyOutlined />} onClick={() => handleCopySubject(current)}>复制</Button></Space>
              </Descriptions.Item>
              <Descriptions.Item label="BOL号">{current.bol_no || '未识别'}</Descriptions.Item>
              <Descriptions.Item label="接收时间">{fmtCN(current.received_at || current.created_at)}</Descriptions.Item>
              <Descriptions.Item label="状态">{current.is_read ? '已读' : '待识别'}</Descriptions.Item>
            </Descriptions>

            {/* 左右对照：左原文(英) / 右译文(中) */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginTop: 16 }}>
              {/* 左：原文 */}
              <div style={{ flex: '1 1 340px', minWidth: 300, display: 'flex', flexDirection: 'column' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <Text strong>邮件原文（英文）</Text>
                  <Tag color="default">原文</Tag>
                </div>
                <div
                  style={{
                    height: DETAIL_HEIGHT,
                    overflowY: 'auto',
                    border: '1px solid #f0f0f0',
                    borderRadius: 6,
                    padding: 14,
                    background: '#fafafa',
                    whiteSpace: 'pre-wrap',
                    lineHeight: 1.8,
                    color: '#333',
                    fontSize: 14,
                  }}
                >
                  {current.body_text || '（无正文）'}
                </div>
              </div>

              {/* 右：AI 译文 */}
              <div style={{ flex: '1 1 340px', minWidth: 300, display: 'flex', flexDirection: 'column' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <Text strong style={{ color: '#0958d9' }}>AI 专业译文（中文）</Text>
                  {current.translation
                    ? <Button size="small" type="primary" ghost loading={translating} onClick={() => handleTranslate(current, true)}>重新翻译</Button>
                    : <Button size="small" type="primary" loading={translating} onClick={() => handleTranslate(current, false)}>一键翻译</Button>}
                </div>
                <div
                  style={{
                    height: DETAIL_HEIGHT,
                    overflowY: 'auto',
                    border: '1px solid #adc6ff',
                    borderRadius: 6,
                    padding: 14,
                    background: '#f0f5ff',
                    whiteSpace: 'pre-wrap',
                    lineHeight: 1.9,
                    color: '#171a20',
                    fontSize: 15,
                  }}
                >
                  {current.translation
                    ? current.translation
                    : <Text type="secondary">暂无译文。点击右上角「一键翻译」，AI 将按物流调度专业术语把邮件正文翻译成中文（签名栏不翻译）。</Text>}
                </div>
              </div>
            </div>
          </>
        )}
      </Drawer>

      {/* 匹配工单弹窗（调度/申诉/索赔三类，互斥） */}
      <Modal
        title="匹配到系统已有工单"
        open={matchOpen}
        onCancel={() => setMatchOpen(false)}
        onOk={handleMatch}
        okText="确认匹配"
        confirmLoading={matching}
        width={560}
      >
        <p style={{ color: '#666' }}>
          匹配后邮件翻译内容将并入该工单操作日志；调度工单「处理中」将自动流转回「待跟进」，申诉/索赔「已完成」将自动重开为「处理中」。
        </p>
        <Radio.Group
          value={matchKind}
          onChange={(e) => { setMatchKind(e.target.value); setMatchOrderId(null); searchOrders(orderSearch, e.target.value); }}
          style={{ marginBottom: 12 }}
          optionType="button"
          buttonStyle="solid"
        >
          <Radio.Button value="order">调度工单</Radio.Button>
          <Radio.Button value="appeal">申诉工单</Radio.Button>
          <Radio.Button value="claim">索赔工单</Radio.Button>
        </Radio.Group>
        <Select
          showSearch
          value={matchOrderId}
          placeholder={`输入${matchKind === 'order' ? '订单号' : matchKind === 'appeal' ? '申诉编号' : '索赔编号'}/客户搜索并选择`}
          style={{ width: '100%' }}
          filterOption={false}
          onSearch={(kw) => searchOrders(kw)}
          onChange={setMatchOrderId}
          onFocus={() => orderOptions.length === 0 && searchOrders(orderSearch)}
          options={orderOptions}
          notFoundContent="无匹配工单"
        />
      </Modal>
    </div>
  );
}
